import { useCallback, useEffect } from 'react'
import { AxiosResponse } from 'axios'
import {
  IItem,
  IProductsV2,
  IResponse,
  patchCoupon,
  sleep,
  triggerLoginEvent,
  useEvents,
  useQueryClient,
  useTrigerEvent
} from '@smuchile/pkg-unimarc-hooks'
import { ICoupon } from '@smuchile/pkg-unimarc-hooks/shared/interfaces/ICoupon'
import { CouponProps, CouponStatus } from '@smuchile/pkg-unimarc-components'
import { transformCouponLineThrough } from '@smuchile/pkg-unimarc-components/helpers'
import { CustomViewError } from 'components/ErrorBoundary'

export const COUPON_DEFAULT_IMAGE =
  'https://images.ctfassets.net/un6yvtd6uq5z/4QSScvaas23f1ucmgxGoDT/04203f555c88f7b67726e33897bda89f/placeholder.png?h=250'

export const statusMap: Record<string, CouponStatus> = {
  '1': undefined,
  '2': 'active',
  '3': 'used'
}

export enum ChannelEnum {
  all = '',
  only_app = 'Exclusivo online',
  only_store = 'Solo tienda'
}

export const getCouponProps = (coupon: ICoupon, index: number): CouponProps => {
  return {
    id: coupon?.id,
    badgeText:
      ChannelEnum[coupon?.entry?.fields?.channel ?? 'all'] ||
      coupon?.entry?.fields?.channel,
    description: coupon?.entry?.fields?.description,
    imageSrc: coupon?.image || coupon?.imageDefault || COUPON_DEFAULT_IMAGE,
    lead: coupon?.entry?.fields?.lead,
    status: statusMap[coupon?.status],
    subtitle: coupon?.entry?.fields?.subTitle,
    title: coupon?.entry?.fields?.title,
    index: index,
    viewProductsText:
      coupon?.entry?.fields?.mechanicId == '11'
        ? 'Ver detalles'
        : 'Ver productos',
    unipay: coupon?.entry?.fields?.unipay,
    ...transformCouponLineThrough(coupon?.entry?.fields?.lineThrough)
  }
}

export const useHandleActiveCoupon = (
  isLoggedIn,
  coupons,
  loadingCoupon,
  selectedCouponId,
  setLoginModalOpen?
) => {
  const { trigger } = useTrigerEvent()
  return useCallback(
    async (couponId: string) => {
      if (!isLoggedIn) {
        setLoginModalOpen && setLoginModalOpen(true)
        triggerLoginEvent({
          eventType: 'loginModal',
          data: { show: true, ref: 'activacion cupon' }
        })
        return
      }
      trigger({
        eventType: 'couponUpdate',
        data: {
          id: couponId,
          isLoading: true
        }
      })

      const activation = await patchCoupon(couponId)

      await sleep(1500)
      if (activation?.data?.error) {
        trigger({
          eventType: 'couponUpdate',
          data: {
            id: couponId,
            isLoading: false
          }
        })
      } else {
        trigger({
          eventType: 'couponUpdate',
          data: {
            id: activation?.data?.data?.id,
            isLoading: false,
            status: activation?.data?.data?.status
          }
        })
      }
    },
    [isLoggedIn, coupons, loadingCoupon, selectedCouponId]
  )
}

export const useLoadingCoupons = (couponsResult) => {
  useEffect(() => {
    if (couponsResult?.data?.status === 404) {
      throw new CustomViewError({
        title: 'Ooops..',
        body1: 'No pudimos cargar tus cupones',
        body2: 'Por favor intenta de nuevo',
        errorType: '404',
        errorImage: {
          width: { desktop: '400px', mobile: '06px' },
          height: { desktop: '408px', mobile: '200px' },
          img: 'https://images.ctfassets.net/un6yvtd6uq5z/1GBIXvn6RU33HcVojo6wy2/4f78e41d4cec8dc21f1de7f3c95a163c/Group_7935.svg',
          title: 'Ooops..'
        },
        onClick: () => {
          return window.location.reload()
        }
      })
    }
  }, [couponsResult])
}

export const useHandleEvents = (setLoadingCoupon) => {
  const queryClient = useQueryClient()
  useEvents({
    eventType: 'couponUpdate',
    callBack: async ({ detail: { id, isLoading, status } }) => {
      if (isLoading) {
        setLoadingCoupon((current) => {
          return [...current, id]
        })
      } else {
        setLoadingCoupon((current) => {
          const currentIndex = current.findIndex((currentCouponId) => {
            return currentCouponId === id
          })

          return [
            ...current.slice(0, currentIndex),
            ...current.slice(currentIndex + 1)
          ]
        })
      }
      if (status) {
        const prevCoupons: AxiosResponse<IResponse<ICoupon[]>> =
          queryClient.getQueryData(['coupons'])
        if (prevCoupons?.data?.data?.length > 0) {
          const couponList = prevCoupons.data.data
          const couponIndex = couponList.findIndex((c) => {
            return c.id === id
          })
          couponList[couponIndex].status = status
          queryClient.removeQueries({ queryKey: 'coupons' })
          queryClient.setQueryData(['coupons'], {
            ...prevCoupons,
            data: {
              ...prevCoupons.data,
              data: [...couponList]
            }
          })
        }
      }
    }
  })
}

export const useFetchCoupons = (
  isLoading,
  coupons,
  isLoggedIn,
  setCouponsLoading,
  couponsResult
) => {
  useEffect(() => {
    if (isLoading) {
      setCouponsLoading(true)
    } else {
      if (couponsResult.data?.headers['x-snap-status'] === 'Pending') {
        setCouponsLoading(true)
        setTimeout(() => {
          couponsResult.refetch()
        }, 1000)
      } else {
        setCouponsLoading(false)
      }
    }
  }, [isLoading, coupons, isLoggedIn])
}

export const mapProductsForCoupons = (
  products: IItem[],
  productsV2: IProductsV2[]
) => {
  return products.map((item) => {
    const itemV2 = productsV2.find((itemV2) => {
      return item?.itemId === itemV2?.item?.itemId
    })
    return {
      ...itemV2,
      ...item,
      detailUrl: item?.slug.split('/')[3]
    }
  })
}
